import React, { useEffect, useState } from 'react';
import { QueryResult, useQuery } from '@apollo/client';
import { fetchSubscriptionsForTxaForWidget } from './queries/fetch-subscriptions-for-txa-for-widget';
import {
  FetchSubscriptionsForTxaForWidgetQuery,
  FetchSubscriptionsForTxaForWidgetQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { RecentlyViewedSubscriptionMetadata } from '@css-xp/ppc-types';
import { RecentlyViewedData } from '@atlassian/cssxp-typescommon';
import { TransactionAccountProductForWidget, SubscriptionQueryItem } from './types';

type TransactionAccountProductNodesMap = Map<string, TransactionAccountProductForWidget>;
type RecentlyViewedSubscriptionData = RecentlyViewedData<RecentlyViewedSubscriptionMetadata>;
type RecentViewedDataNodesMap = Map<string, RecentlyViewedSubscriptionData>;

type ChildrenProp = (
  args: Omit<
    QueryResult<FetchSubscriptionsForTxaForWidgetQuery, FetchSubscriptionsForTxaForWidgetQueryVariables>,
    'data'
  > & { data: SubscriptionQueryItem[] | null | undefined }
) => JSX.Element;

export const RecentlyViewedSubscriptionsQueryWrapper = ({
  txaId,
  recentlyViewedData,
  children,
}: {
  txaId: string;
  children: ChildrenProp;
  recentlyViewedData: RecentlyViewedSubscriptionData[];
}): JSX.Element => {
  const [recentlyViewedWidgetData, setRecentlyViewedWidgetData] = useState<SubscriptionQueryItem[]>();
  const recentlyViewedDataNodesMap: RecentViewedDataNodesMap = React.useMemo(
    () =>
      recentlyViewedData.reduce((acc: RecentViewedDataNodesMap, recentData: RecentlyViewedSubscriptionData) => {
        if (!recentData.metadata || !recentData.metadata.id) {
          return acc;
        }
        acc.set(recentData.metadata.id, recentData);
        return acc;
      }, new Map()),
    [recentlyViewedData]
  );

  const subscriptionIds: string[] = [];
  for (const key of recentlyViewedDataNodesMap.keys()) {
    subscriptionIds.push(key);
  }

  const { data, ...args } = useQuery<
    FetchSubscriptionsForTxaForWidgetQuery,
    FetchSubscriptionsForTxaForWidgetQueryVariables
  >(fetchSubscriptionsForTxaForWidget, {
    variables: {
      id: txaId,
      filterOptions: [
        {
          key: 'idIsIn',
          values: subscriptionIds,
        },
      ],
    },
    skip: !txaId || !subscriptionIds || !subscriptionIds.length,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    const nodes = data?.transactionAccount?.productSubscriptionsInPPC?.nodes;
    if (!nodes || !nodes.length) {
      return;
    }
    const productNodesMap: TransactionAccountProductNodesMap = nodes.reduce(
      (acc: TransactionAccountProductNodesMap, node: TransactionAccountProductForWidget | null) => {
        if (!node || !node.id) {
          return acc;
        }
        acc.set(node.id, node);
        return acc;
      },
      new Map()
    );
    // making the nodes order same as it's passed from props and re-assigning it
    const productNodes = subscriptionIds
      .filter((id) => !!productNodesMap.has(id))
      .map((id) => ({
        ...productNodesMap.get(id),
        timestamp: recentlyViewedDataNodesMap.get(id)?.timestamp,
      }));

    setRecentlyViewedWidgetData(productNodes as SubscriptionQueryItem[]);
  }, [data, recentlyViewedData]);

  return children({
    data: recentlyViewedWidgetData,
    ...args,
  });
};
